<template>
  <div class="add-record-layout-div">
    <div class="sideBarHeading">
      <h5 class="heading">{{ adminSideBarHeading }}</h5>
      <p class="heading-content">{{ `Manage your ${adminSideBarHeading} here. ${adminSideBarHeading} are available for
        selection during a Tumor
        Board Session.`}}</p>
    </div>
    <div class="sideBarHeading">
      <h5 class="heading">{{ `Add ${adminSideBarHeading}` }}</h5>
      <div class="input-button-align">
        <InputText v-model="enteredAdminRecordData" size="small" @input="clearErrorMessage()"
          :placeholder="`Enter ${adminSideBarHeading}`" @change="$emit('form-change')" />
        <button class="primary-filled-button" size="small" @click="addAdminRecordDataFunction()">Add</button>
      </div>
      <div class="error-message-div">
        <p>{{ addAdminRecordDataErrorMessage }}</p>
      </div>
    </div>
    <div v-if="adminRecordData.length" class="sideBarHeading">
      <h5 class="heading">{{ `Current ${adminSideBarHeading}` }}</h5>
      <div v-for="(adminRecord, index) in adminRecordData" :key="adminRecord[`${props.componentProps.recordId}_id`]">
        <div class="current-admin-record-div">
          <div>
            <InputText v-model="adminRecord[props.componentProps.recordName]" size="small"
              :disabled=!adminRecord.is_active :readonly="!adminRecord.isEditable" @blur="onBlurInput(adminRecord)"
              @input="clearErrorMessage(adminRecord)" @change="$emit('form-change')" />
          </div>
          <div v-if="!adminRecord.showSaveButton" title="Enable/Disable" class="input-switch-div">
            <InputSwitch v-model="adminRecord.is_active" size="small" @change="toggleSwitchChange(adminRecord)" />
          </div>
          <div :class="{ 'save-padding': adminRecord.showSaveButton }">
            <span v-if="!adminRecord.showSaveButton" class="material-symbols-outlined icon-button-to-primary"
              @click="editAdminRecordData(adminRecord, index)" :class="{ 'disabled': !adminRecord.is_active }"
              title="Edit">edit</span>
            <span v-if="adminRecord.showSaveButton" class="material-symbols-outlined icon-button-to-primary"
              @click="onClickSaveFunction(adminRecord, adminRecord.initialRecordName)" title="Save">save</span>
          </div>
        </div>
        <div class="error-message-div">
          <p v-if="adminRecord.errorMessage">{{ adminRecord.errorMessage }}</p>
        </div>
      </div>
    </div>
    <div>
      <Dialog v-model:visible="isDialogVisible" modal header="Confirmation">
        <p>{{ dynamicDialogContent }}</p>
        <div class="flex gap-2">
          <button class="primary-outlined-button cancel-btn" @click.prevent="handleDialogCancel()">Cancel</button>
          <button class="primary-filled-button save-btn" @click.prevent="handleDialogConfirm()">Confirm</button>
        </div>
      </Dialog>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, defineProps, onMounted, ref } from 'vue'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import InputSwitch from 'primevue/inputswitch'
import { getAdminRecordData } from './getAdminRecordData'
import { patchAdminRecordData } from './patchAdminRecordData'
import { addAdminRecordDataAPI } from './addAdminRecordDataAPI'
import { showToast } from '@/utils/utils'

const props = defineProps({
  componentProps: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['form-change', 'reset-confirmation'])

const adminSideBarHeading = computed(() => props.componentProps.adminSideBarHeading)

const adminRecordData = ref([])
const initialAdminRecordData = ref([])
const enteredAdminRecordData = ref('')
const addAdminRecordDataErrorMessage = ref('')
const isDialogVisible = ref(false)
const selectedAdminRecord = ref({})
const dynamicDialogContent = ref('')

onMounted(async () => {
  await reloadAdminDataRecord()
})

const reloadAdminDataRecord = async () => {
  const adminRecordApiResponse = await getAdminRecordData(props.componentProps.recordApi)
  adminRecordData.value = adminRecordApiResponse.map((rec) => ({ ...rec, initialRecordName: rec[props.componentProps.recordName], isEditable: false, showSaveButton: false, errorMessage: '' }))
  initialAdminRecordData.value = [...adminRecordApiResponse]
}

const editAdminRecordData = (adminRecord) => {
  adminRecord.isEditable = true
  adminRecord.showSaveButton = true
  adminRecord.initialRecordName = adminRecord[props.componentProps.recordName]
}

const toggleSwitchChange = (adminRecord) => {
  dynamicDialogContent.value = adminRecord.is_active ? `Do you want to enable '${adminRecord[props.componentProps.recordName]}' ?` : `Do you want to disable '${adminRecord[props.componentProps.recordName]}' ?`
  adminRecord.is_active = !adminRecord.is_active
  selectedAdminRecord.value = adminRecord
  isDialogVisible.value = true
}

const onClickSaveFunction = async (adminRecord, currentRecordName) => {
  if (adminRecord[props.componentProps.recordName] !== currentRecordName && hasDuplicate(adminRecord[props.componentProps.recordName])) {
    adminRecord.errorMessage = 'This record already exists'
    return
  }
  if (adminRecord.errorMessage) { return }
  delete adminRecord.isEditable
  delete adminRecord.showSaveButton
  delete adminRecord.errorMessage
  delete adminRecord.initialRecordName
  const adminRecordPayload = [adminRecord]
  const patchAdminRecordDataResponse = await patchAdminRecordData(adminRecordPayload, props.componentProps.recordApi)
  if (patchAdminRecordDataResponse.detail) {
    showToast('error', `Error saving the record ${adminRecord[props.componentProps.recordName]}`)
    return
  }
  showToast('success', 'Changes Saved')
  await reloadAdminDataRecord()
}

const handleDialogCancel = () => {
  isDialogVisible.value = false
}

const handleDialogConfirm = async () => {
  delete selectedAdminRecord.value.isEditable
  delete selectedAdminRecord.value.showSaveButton
  delete selectedAdminRecord.value.errorMessage
  delete selectedAdminRecord.value.initialRecordName
  selectedAdminRecord.value.is_active = !selectedAdminRecord.value.is_active
  const adminRecordPayload = [selectedAdminRecord.value]
  await patchAdminRecordData(adminRecordPayload, props.componentProps.recordApi)
  isDialogVisible.value = false
}

const addAdminRecordDataFunction = async () => {
  if (hasDuplicate(enteredAdminRecordData.value)) {
    addAdminRecordDataErrorMessage.value = 'This record already exists'
    return
  }
  const errorMessage = validateInput(enteredAdminRecordData.value)
  if (errorMessage) {
    addAdminRecordDataErrorMessage.value = errorMessage
    return
  }
  const adminRecordPayload = [{
    [props.componentProps.recordName]: enteredAdminRecordData.value,
    is_active: true,
  }]
  const addAdminRecordDataAPIResponse = await addAdminRecordDataAPI(adminRecordPayload, props.componentProps.recordApi)
  if (addAdminRecordDataAPIResponse.detail) {
    showToast('error', `Error adding the record ${enteredAdminRecordData.value}`)
    return
  }
  emit('reset-confirmation')
  showToast('success', 'Changes Saved')
  await reloadAdminDataRecord()
  enteredAdminRecordData.value = ''
}

const onBlurInput = (adminRecord) => {
  adminRecord.errorMessage = validateInput(adminRecord[props.componentProps.recordName])
}

const validateInput = (value) => {
  const trimmedValue = value.trim()
  if (!trimmedValue) { return 'Input value cannot be empty' }
  if (trimmedValue.length > 100) { return 'Input cannot exceed 100 characters' }
  return ''
}

const hasDuplicate = (value) => {
  const isDuplicateNamePresent =
    initialAdminRecordData.value.find(
      (element) => element[props.componentProps.recordName].trim() === value.trim(),
    )
  return !!isDuplicateNamePresent
}

const clearErrorMessage = (adminRecord) => {
  adminRecord ? adminRecord.errorMessage = '' : addAdminRecordDataErrorMessage.value = ''
}
</script>

<style scoped>
.current-admin-record-div {
  margin-bottom: 20px;
  display: flex;
  width: 60%;
  align-items: center;
}

.add-record-layout-div {
  margin-left: 5%;
}

.sideBarHeading {
  width: 40%;
  text-align: initial;
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  color: var(--Grey600);
}

.error-message-div p {
  color: var(--ErrorRedDark);
}

.input-switch-div {
  margin-left: 14px;
  margin-top: 3px;
}

.heading {
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  margin-left: 0;
}

.heading-content {
  color: var(--Grey600);
  font-size: 0.9rem;
  font-style: normal;
  font-weight: 300;
}

.input-button-align {
  display: flex;
  align-items: center;
}

.save-padding {
  margin-left: 7px;
}
</style>

<style>
.current-admin-record-div .p-inputswitch {
  width: 2.7rem;
  height: 1.2rem;
}

.current-admin-record-div .p-inputswitch .p-inputswitch-slider:before {
  width: 1rem;
  height: 1rem;
  margin-top: -0.5rem;
}
</style>
